import { startRegistration } from "@simplewebauthn/browser";
import { useEffect, useState } from "react";
import { api } from "./api.ts";
import { Button, Dialog, ErrorText } from "./ui.tsx";

/**
 * One tap on every device: after someone confirms with a code on a phone or computer that has
 * Face ID, Touch ID or Windows Hello, recoup offers to add a passkey for it, so next time it's a
 * fingerprint or a glance instead of a code. Each device gets its own; the authenticator app keeps
 * working everywhere. Offered once per browser: "Not now" and "added" are remembered there.
 */
const KEY = "recoup.devicePasskey";

const remembered = () => {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
};
export const remember = (v: "added" | "declined") => {
  try {
    localStorage.setItem(KEY, v);
  } catch {
    /* storage unavailable: it may be offered again, which is harmless */
  }
};

/** This device, as people call it: names the passkey and the offer. */
export function deviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android phone";
  if (/Mac/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows computer";
  return "this computer";
}

const unlock = (name: string) =>
  /iPhone|iPad/.test(name)
    ? "Face ID or Touch ID"
    : name === "Mac"
      ? "Touch ID"
      : name === "Windows computer"
        ? "Windows Hello"
        : "your fingerprint or screen lock";

/** Call after a code worked: offers the device's own passkey, if it can have one. */
export async function offerDevicePasskey(): Promise<void> {
  if (remembered()) return;
  try {
    if (!(await window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable?.()))
      return;
  } catch {
    return;
  }
  window.dispatchEvent(new Event("recoup:device-passkey"));
}

/** Mounted once at the app root, beside the step-up dialog. */
export function DevicePasskeyOffer() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const name = deviceName();
  useEffect(() => {
    const show = () => {
      setError(null);
      setOpen(true);
    };
    window.addEventListener("recoup:device-passkey", show);
    return () => window.removeEventListener("recoup:device-passkey", show);
  }, []);
  const notNow = () => {
    remember("declined");
    setOpen(false);
  };
  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      const optionsJSON =
        await api.post<Parameters<typeof startRegistration>[0]["optionsJSON"]>(
          "/auth/passkeys/options",
        );
      const response = await startRegistration({ optionsJSON });
      await api.post("/auth/passkeys", { response, name });
      remember("added");
      setOpen(false);
    } catch (e) {
      setError(
        e instanceof Error && e.name === "NotAllowedError"
          ? new Error("The passkey window was closed. Try again, or choose Not now.")
          : e,
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      title={`Skip the code on this ${name}?`}
      onClose={notNow}
      footer={
        <>
          <Button variant="ghost" onClick={notNow}>
            Not now
          </Button>
          <Button variant="primary" busy={busy} onClick={add}>
            Use {unlock(name)}
          </Button>
        </>
      }
    >
      <p className="text-muted">
        Next time recoup asks it's you on this {name}, use {unlock(name)} instead of typing a code.
        Your authenticator app keeps working on every device, and you can do this on each phone and
        computer you use.
      </p>
      <ErrorText error={error} />
    </Dialog>
  );
}
